<template>
    <div class="template-messages">
        <TemplateMessageModal
            v-if="templateMessageModal && !isMetaFormat(templateMessageModal)"
            :template="templateMessageModal"
            :channel-id="contact.channel"
            @close="templateMessageModal = null"
            @send="sendTemplate"
        />
        <MetaTemplateMessageModal
            v-if="templateMessageModal && isMetaFormat(templateMessageModal)"
            :template="templateMessageModal"
            :channel="currentChannel"
            @close="templateMessageModal = null"
            @send="sendTemplate"
        />
        <div class="empty" v-if="channels.length === 0">
            <div class="title">{{ $t("auto.whatsappChannelNotFound") }}</div>
            <div class="message">{{ $t("auto.noWhatsappChannelFoundInSystem") }}</div>
        </div>
        <template v-else>
            <div class="search-container">
                <input
                    v-model="searchTerm"
                    type="text"
                    class="search-input"
                    :placeholder="$t('auto.searchTemplate')"
                    @input="onSearchInput"
                />
            </div>
            <div class="items">
                <div v-if="filteredTemplates.length <= 0">{{ $t("auto.templateNotFound") }}</div>
                <div
                    class="item-card"
                    v-for="(item, index) of filteredTemplates"
                    :data-id="item._id"
                    :data-pointer="index"
                    :key="item._id"
                    @click="useTemplate(item)"
                    :class="{
                        selected: pointerIndex === index,
                        hoverSelected: pointerIndex === index,
                    }"
                >
                    <div class="title-wrapper">
                        <div class="title">
                            {{ getTemplateName(item) }}
                        </div>
                        <div class="status-badge" :class="getStatusClass(item)" v-if="isMetaFormat(item)">
                            {{ getTemplateStatus(item) }}
                        </div>
                    </div>
                    <div class="message-preview">
                        {{ getTemplateBody(item) }}
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>
<script>
import Vue from "vue";
import { mapActions, mapState } from "vuex";
import log2 from "../../../../../../utils/log2";
import TemplateMessageModal from "./TemplateMessageModal.vue";
import MetaTemplateMessageModal from "./MetaTemplateMessageModal.vue";
import Fuse from "fuse.js";

const log = log2("TemplateMessages");
export default Vue.extend({
    name: "TemplateMessages",
    components: { TemplateMessageModal, MetaTemplateMessageModal },
    data() {
        return {
            pointerIndex: 0,
            cancels: [],
            channelTemplates: [],
            templateMessageModal: null,
            searchTerm: "",
        };
    },
    props: {
        type: {
            type: String,
        },
        contact: {
            type: Object,
        },
    },
    computed: {
        ...mapState({
            businessTemplates: (state) => state.whatsapp.templates,
            channels: (state) => state.channel.channels.filter((channel) => 
                channel.type === "whatsapp-business-api" || channel.type === "whatsapp-business-api-supsis"
            ),
        }),
        currentChannel() {
            return this.channels.find((channel) => channel._id === this.contact.channel);
        },
        filteredTemplates() {
            let templates = this.channelTemplates;
            
            // Meta format olanlar için sadece APPROVED olanları göster
            templates = templates.filter(t => {
                // Meta format olanlar için APPROVED kontrolü
                if (this.isMetaFormat(t)) {
                    return t.status === "APPROVED";
                }
                // Meta format olmayanlar için filtreleme yapma
                return true;
            });
            
            if (!this.searchTerm.trim()) {
                return templates;
            }
            return this.search(this.searchTerm);
        },
    },
    async mounted() {
        this.getTemplates(this.contact.channel);
        const channel = this.channels.find((channel) => channel._id === this.contact.channel);
        const initialTemplateId = _.get(channel, "payload.initialTemplate.id");
        this.channelTemplates = this.businessTemplates[this.contact.channel] || [];
        const initialTemplate = this.channelTemplates.find((item) => item.id === initialTemplateId);

        if (initialTemplate) {
            this.channelTemplates = [
                initialTemplate,
                ...this.channelTemplates.filter((item) => item.id !== initialTemplateId),
            ];
        }

        const keydownCallback = (e) => {
            if (e.key === "ArrowDown") {
                const index = Math.min(this.pointerIndex + 1, this.filteredTemplates.length - 1);
                const id = this.filteredTemplates.find((item, i) => i === index)?._id;
                const el = document.querySelector(`div[data-id='${id}']`);
                el?.scrollIntoView({
                    behavior: "smooth",
                });
                this.pointerIndex = index;
            } else if (e.key === "ArrowUp") {
                const index = Math.max(this.pointerIndex - 1, 0);
                const id = this.filteredTemplates.find((item, i) => i === index)?._id;
                const el = document.querySelector(`div[data-id='${id}']`);
                el?.scrollIntoView({
                    behavior: "smooth",
                });
                this.pointerIndex = index;
            } else if (e.key === "Enter") {
                const item = this.filteredTemplates.find((item, i) => i === this.pointerIndex);
                this.useTemplate(item);
            }
        };
        window.addEventListener("keydown", keydownCallback);
        this.cancels.push(() => window.removeEventListener("keydown", keydownCallback));
    },
    created() {},
    methods: {
        ...mapActions({
            getTemplates: "whatsapp/getTemplates",
        }),
        search(query) {
            let templates = this.channelTemplates;
            
            // Meta format olanlar için sadece APPROVED olanları göster
            templates = templates.filter(t => {
                // Meta format olanlar için APPROVED kontrolü
                if (this.isMetaFormat(t)) {
                    return t.status === "APPROVED";
                }
                // Meta format olmayanlar için filtreleme yapma
                return true;
            });
            
            const fuse = new Fuse(templates, {
                includeScore: true,
                keys: ["elementName", "data", "name"]
            });
            return fuse.search(query).map((x) => x.item);
        },
        onSearchInput() {
            this.pointerIndex = 0;
        },
        useTemplate(item) {
            this.templateMessageModal = item;
        },
        sendTemplate(data) {
            this.templateMessageModal = null;
            this.$emit("send", data);
        },
        isMetaFormat(item) {
            return item.format === "meta";
        },
        getTemplateName(item) {
            if (this.isMetaFormat(item)) {
                return item.name || "";
            }
            return item.elementName || "";
        },
        getTemplateBody(item) {
            if (this.isMetaFormat(item)) {
                const components = item.components || [];
                const bodyComponent = components.find((c) => c.type === "BODY");
                return bodyComponent ? bodyComponent.text : "";
            }
            return item.data || "";
        },
        getTemplateStatus(item) {
            return item.status || "PENDING";
        },
        getStatusClass(item) {
            const status = this.getTemplateStatus(item);
            if (status === "APPROVED") return "status-approved";
            if (status === "REJECTED") return "status-rejected";
            return "status-pending";
        },
    },
    beforeDestroy() {
        log("beforeDestroy");
        this.cancels.forEach((cancel) => cancel());
    },
    watch: {
        pointerIndex(val) {
            const el = this.$el.querySelector(`.item-card[data-pointer="${val}"]`);
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
            }
        },
    },
});
</script>

<style scoped lang="scss">
.template-messages {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
    height: 100%;
    min-height: inherit;

    &.search-mode {
        .groups {
            display: none;
        }

        .items {
            width: 100%;
        }
    }

    .empty {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 10px;
        height: 100%;
        width: 100%;

        .title {
            font-size: 16px;
            font-weight: 700;
            color: #6c757d;
        }

        .message {
            font-size: 14px;
            font-weight: 500;
            color: #6c757d;
        }
    }

    .search-container {
        padding: 8px;
        border-bottom: 1px solid #f1f4f7;

        .search-input {
            width: 100%;
            padding: 8px 12px;
            background: #f2f4f7;
            border: 1px solid #cccccc;
            border-radius: 4px;
            font-size: 14px;
            outline: none;

            &:focus {
                border-color: #22272d;
                box-shadow: 0 0 0 2px rgba(34, 39, 45, 0.3);
            }

            &::placeholder {
                color: #9ca3af;
            }
        }
    }

    .items {
        display: flex;
        flex-direction: column;
        width: 100%;
        background-color: #fff;
        overflow: auto;

        .item-card {
            display: flex;
            flex-direction: column;
            gap: 3px;
            padding: 5px 5px;
            cursor: pointer;
            border-bottom: 1px solid #f1f4f7;

            &:hover,
            &.selected {
                background-color: #f5f5f5;
            }

            &.hoverSelected {
                box-shadow: 2px 2px 2px 2px #22272d;
            }

            .title-wrapper {
                display: flex;
                justify-content: space-between;
                align-items: center;

                .title {
                    font-size: 14px;
                    font-weight: 700;
                    color: #6c757d;
                    display: flex;
                    align-items: center;

                    span {
                        display: flex;
                        align-items: center;
                    }
                }

                .shortcut {
                    font-size: 12px;
                    font-weight: 400;
                    color: #6c757d;
                }

                .status-badge {
                    font-size: 10px;
                    padding: 2px 6px;
                    border-radius: 4px;
                    font-weight: 600;
                    text-transform: uppercase;

                    &.status-approved {
                        background: #e6ffed;
                        color: #52c41a;
                    }

                    &.status-rejected {
                        background: #fff1f0;
                        color: #f5222d;
                    }

                    &.status-pending {
                        background: #fff7e6;
                        color: #fa8c16;
                    }
                }
            }

            .message-preview {
                font-size: 12px;
                font-weight: 500;
                color: #6c757d;
                -webkit-line-clamp: 2;
                overflow: hidden;
                display: -webkit-box;
                -webkit-box-orient: vertical;
                border-left: 3px solid #6c757d;
                padding-left: 3px;
            }
        }
    }
}
</style>
